import * as React from 'react';
import styled from 'styled-components';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { Logo } from '../../atoms';

export const SIDEBAR_EXPANDED_WIDTH = 224;
export const SIDEBAR_COLLAPSED_WIDTH = 72;
const STORAGE_KEY = 'ui:sidebar:collapsed';

const readCollapsed = (): boolean => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw !== null) return raw === '1';
  } catch {
    /* noop */
  }
  return typeof window !== 'undefined' && window.innerWidth < 1024;
};

interface SidebarContextValue {
  collapsed: boolean;
}

const SidebarContext = React.createContext<SidebarContextValue>({
  collapsed: false,
});

export const useSidebar = () => React.useContext(SidebarContext);

interface Props {
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const SidebarShell = ({ children, footer }: Props) => {
  const [collapsed, setCollapsed] = React.useState<boolean>(
    readCollapsed,
  );

  React.useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [collapsed]);

  const toggle = React.useCallback(
    () => setCollapsed(prev => !prev),
    [],
  );

  const ctx = React.useMemo(() => ({ collapsed }), [collapsed]);

  return (
    <SidebarContext.Provider value={ctx}>
      <Aside $collapsed={collapsed} aria-label="Primary navigation">
        <LogoRow $collapsed={collapsed}>
          <LogoBox $collapsed={collapsed}>
            <Logo />
          </LogoBox>
        </LogoRow>

        <Nav>{children}</Nav>

        {footer && <Footer>{footer}</Footer>}

        <CollapseButton
          type="button"
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </CollapseButton>
      </Aside>
    </SidebarContext.Provider>
  );
};

const Aside = styled.aside<{ $collapsed: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  width: ${({ $collapsed }) =>
    $collapsed
      ? `${SIDEBAR_COLLAPSED_WIDTH}px`
      : `${SIDEBAR_EXPANDED_WIDTH}px`};
  min-width: ${({ $collapsed }) =>
    $collapsed
      ? `${SIDEBAR_COLLAPSED_WIDTH}px`
      : `${SIDEBAR_EXPANDED_WIDTH}px`};
  background: var(--primary-800);
  color: var(--neutral-0);
  transition: width var(--motion-slow) var(--ease-standard),
    min-width var(--motion-slow) var(--ease-standard);
  overflow: hidden;

  @media (max-width: 840px) {
    width: ${SIDEBAR_COLLAPSED_WIDTH}px;
    min-width: ${SIDEBAR_COLLAPSED_WIDTH}px;
  }
`;

const LogoRow = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 ${({ $collapsed }) => ($collapsed ? '0' : 'var(--space-4)')};
  justify-content: ${({ $collapsed }) =>
    $collapsed ? 'center' : 'flex-start'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const LogoBox = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  height: ${({ $collapsed }) => ($collapsed ? '44px' : '52px')};

  a,
  > a {
    display: inline-flex;
    align-items: center;
    height: 100%;
  }

  img {
    height: 100%;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: var(--space-3) var(--space-2);
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const Footer = styled.div`
  padding: var(--space-3) var(--space-2);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const CollapseButton = styled.button`
  position: absolute;
  right: -12px;
  top: 80px;
  width: 24px;
  height: 24px;
  border-radius: var(--radius-full);
  border: 1px solid var(--neutral-200);
  background: var(--neutral-0);
  color: var(--neutral-600);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--shadow-sm);
  z-index: 2;
  transition: background-color var(--motion-fast) var(--ease-standard),
    color var(--motion-fast) var(--ease-standard);

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    background: var(--primary-50);
    color: var(--primary-700);
  }

  &:focus-visible {
    box-shadow: var(--shadow-focus);
  }

  @media (max-width: 840px) {
    display: none;
  }
`;

export default SidebarShell;
